import { Component, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CmUiDialogService, CmUiStandardButtonComponent } from '@cm/ui-modules';
import { saveAs } from 'file-saver';
import { AsyncTaskService, AsyncTask } from '../../services/async-task.service';
import { MediaLibraryService, MediaDeleteResult } from '../../services/media-library.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { firstValueFrom, take } from 'rxjs';
import { CcPreviewModalComponent } from '../../../modules/media-library/components/cc-preview-modal/cc-preview-modal.component';
import { VideoClipManagerComponent } from '../../../modules/media-library/components/video-clip-manager/video-clip-manager.component';
import { MergePreviewDialogComponent } from '../../../modules/media-library/components/merge-preview-dialog/merge-preview-dialog.component';
import { AUDIT_REPORT_SESSION_KEY, AuditReportAsset, AuditReportData } from '../../../modules/media-library/components/audit-report-page/audit-report-page.component';
import { LabelSuggestionDialogComponent } from '../../../modules/media-library/components/label-suggestion-dialog/label-suggestion-dialog.component';


/**
 * Global progress toast component for async tasks
 * Displays in bottom-left corner showing progress of all active tasks
 */
@Component({
  selector: 'app-async-task-progress',
  standalone: true,
  imports: [CommonModule, TranslateModule, CmUiStandardButtonComponent],
  templateUrl: './async-task-progress.component.html',
  styleUrls: ['./async-task-progress.component.scss']
})
export class AsyncTaskProgressComponent implements OnInit, OnDestroy {
  asyncTaskService = inject(AsyncTaskService);
  router = inject(Router);
  mediaLibraryService = inject(MediaLibraryService);
  // Two dialog handles on purpose: the heavy media-library editors below are
  // permanently on MatDialog, while dialogs built on cm-ui-dialog must go through
  // CmUiDialogService or they render without the shared surface.
  dialog = inject(MatDialog);
  private cmDialog = inject(CmUiDialogService);
  private translate = inject(TranslateService);
  private http = inject(HttpClient);
  private snackBarService = inject(SnackBarService);

  private readonly currentTime = signal(Date.now());
  private clockInterval?: ReturnType<typeof setInterval>;

  ngOnInit(): void {
    this.clockInterval = setInterval(() => this.currentTime.set(Date.now()), 1000);
  }

  ngOnDestroy(): void {
    clearInterval(this.clockInterval);
  }

  /**
   * Get status text
   */
  getStatusText(status: AsyncTask['status']): string {
    switch (status) {
      case 'pending':    return this.translate.instant('async_task.status.starting');
      case 'processing': return this.translate.instant('async_task.status.processing');
      case 'completed':  return this.translate.instant('async_task.status.completed');
      case 'failed':     return this.translate.instant('async_task.status.failed');
      default:           return status;
    }
  }

  /**
   * Shortfall line for a bulk delete that finished without taking every asset.
   *
   * highp-media keeps going past a failed asset and only fails the whole task when not one
   * could be deleted, so a green card can still hide "8 of 12". That used to be announced by
   * a snack-bar, which duplicated the card for every other outcome; it belongs here, on the
   * card that already owns the task's story. Null for anything else, including a clean run.
   */
  getDeleteSummary(task: AsyncTask): string | null {
    if (task.type !== 'media-delete') {
      return null;
    }
    const result = task.result as MediaDeleteResult | undefined;
    if (!result?.failed?.length) {
      return null;
    }
    return this.translate.instant('media_library.assets.deleted_partial', {
      deleted: result.deleted,
      requested: result.requested,
      failed: result.failed.length,
    });
  }

  /**
   * Get task duration
   */
  getTaskDuration(task: AsyncTask): string {
    const start = new Date(task.startedAt).getTime();
    const end = task.completedAt ? new Date(task.completedAt).getTime() : this.currentTime();
    const duration = Math.floor((end - start) / 1000);

    if (duration < 60) {
      return `${duration}s`;
    } else {
      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;
      return `${minutes}m ${seconds}s`;
    }
  }

  /**
   * Remove a task
   */
  removeTask(taskId: string): void {
    this.asyncTaskService.removeTask(taskId);
  }

  /**
   * Clear all completed tasks
   */
  clearCompleted(): void {
    this.asyncTaskService.clearCompletedTasks();
  }

  /**
   * Clear all failed tasks
   */
  clearFailed(): void {
    this.asyncTaskService.clearFailedTasks();
  }

  /**
   * Handle action button click
   */
  async handleAction(task: AsyncTask): Promise<void> {
    if (task.resultUrl) {
      // File-producing task (activities export, campaign bulk export): fetch the finished
      // file again. The automatic save at completion time only reaches the user if they
      // happened to be there for it.
      const saved = await this.asyncTaskService.downloadResult(task.task_id);
      if (!saved) {
        this.snackBarService.show('async_task.error.resultUnavailable', 'fail');
      }
      return;
    }

    if (task.type === 'autocrop') {
      // Extract the vertical variant UUID from autocrop result
      const verticalUuid = task.result?.vertical_variant?.uuid;

      if (verticalUuid) {
        // Navigate to media library and pass the UUID as a query parameter
        this.router.navigate(['/medialibrary'], {
          queryParams: { selectAsset: verticalUuid }
        });
      } else {
        // If no vertical variant, just open media library
        this.router.navigate(['/medialibrary']);
      }
    } else if (task.type === 'speech-to-text' || task.type === 'speech-to-words') {
      // For transcript tasks, open transcript modal directly
      const mediaAssetUuid = task.metadata?.mediaAssetUuid;

      if (mediaAssetUuid) {
        try {
          // speech-to-text stores vtt + transcript inline in task.result — use it directly
          if (task.type === 'speech-to-text' && task.result?.vtt && task.result?.transcript) {
            await this.mediaLibraryService.openTranscriptForAsset(
              {
                uuid: mediaAssetUuid,
                metadata: {
                  vtt: task.result.vtt,
                  txt: task.result.transcript,
                  vttFile: task.result.vtt_file,
                },
              },
              this.dialog
            );
          } else {
            const asset = await firstValueFrom(this.mediaLibraryService.getMediaAssetByUuid(mediaAssetUuid));
            if (asset) {
              await this.mediaLibraryService.openTranscriptForAsset(
                this.mediaLibraryService.mapAssetToMediaAsset(asset),
                this.dialog
              );
            }
          }
        } catch (error) {
          console.error('Error fetching asset for transcript:', error);
          // Fallback: navigate to media library
          this.router.navigate(['/medialibrary'], {
            queryParams: { selectAsset: mediaAssetUuid }
          });
        }
      }
    } else if (task.type === 'generate-cc') {
      const outputFile = task.result?.output_file;
      const name = task.result?.name || outputFile;
      if (outputFile) {
        const ccDialogRef = this.dialog.open(CcPreviewModalComponent, {
          width: '800px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          data: {
            outputFile,
            name,
            originalAssetName: task.metadata?.['assetName'],
          },
        });
        ccDialogRef.afterClosed().subscribe(result => {
          if (result?.saved) {
            localStorage.setItem('mediaLibraryType', 'video');
            localStorage.removeItem('mediaLibraryPersonal');
            this.router.navigate(['/medialibrary'], { queryParams: { type: 'video' } });
          }
        });
      }
    } else if (task.type === 'medvideo-extract') {
      const mediaAssetUuid = task.metadata?.mediaAssetUuid;
      if (mediaAssetUuid && task.result) {
        try {
          const asset = await firstValueFrom(this.mediaLibraryService.getMediaAssetByUuid(mediaAssetUuid));
          if (asset) {
            const mappedAsset = this.mediaLibraryService.mapAssetToMediaAsset(asset);
            const clipManagerRef = this.dialog.open(VideoClipManagerComponent, {
              data: { asset: mappedAsset, result: task.result },
              width: '90vw',
              maxWidth: '1400px',
              maxHeight: '90vh',
              panelClass: 'video-clip-manager-dialog',
            });
            clipManagerRef.afterClosed().subscribe(result => {
              if (result?.saved) {
                this.mediaLibraryService.refresh$.next();
              }
            });
          }
        } catch (error) {
          console.error('[AsyncTaskProgress] Error opening clip manager:', error);
        }
      }
    } else if (task.type === 'media-merge' || task.type === 'media-merge-save') {
      // The merge produced a draft — this dialog is the only way to watch it, name it
      // and promote it (or throw it away). `media-merge-save` reaches here only when
      // a save FAILED and ai-search restored the draft, so the retry opens the same
      // dialog. A dismissal deletes the draft and the card has nothing left to show;
      // a confirm hands the SAME task id to the save phase, so the card must survive
      // to follow it — removing it there would drop the user's only view of a
      // conversion that takes minutes. A failed confirm keeps the dialog open.
      const mergeDialogRef = this.cmDialog.open(MergePreviewDialogComponent, {
        data: { task },
        width: '900px',
        maxWidth: '95vw',
        maxHeight: '90vh',
      });
      mergeDialogRef.closed.pipe(take(1)).subscribe((result) => {
        if (!(result as { confirmed?: boolean } | undefined)?.confirmed) {
          this.asyncTaskService.removeTask(task.task_id);
        }
      });
    } else if (task.type === 'campaign-transcript-report' && task.result) {
      const auditData = this.transformTranscriptToAuditData(task.result, task.startedAt);
      sessionStorage.setItem(AUDIT_REPORT_SESSION_KEY, JSON.stringify(auditData));
      window.open('/app/medialibrary/audit-report', '_blank');
    } else if (task.type === 'billing-backfill') {
      this.router.navigate(['/billing']);
    } else if (task.type === 'media-autolabel') {
      // Proposed new labels wait for a human decision — review them per asset.
      LabelSuggestionDialogComponent.open(this.cmDialog, { batchUuid: task.task_id });
    }
  }

  handleDownloadErrors(task: AsyncTask): void {
    const uuid = task.result?.uuid;
    if (!uuid) return;
    this.http.get(`/api/core/contact/import/failed/${uuid}`, { responseType: 'blob' })
      .subscribe({
        next: blob => saveAs(blob, `import-errors-${uuid}.xlsx`),
        error: error => console.error('[AsyncTaskProgress] Error downloading import errors:', error),
      });
  }

  handleDownloadLog(task: AsyncTask): void {
    const uuid = task.result?.uuid;
    const url = task.result?.logDownloadUrl ?? (uuid ? `/api/core/contact/import/log/${uuid}` : null);
    if (!url) return;
    this.http.get(url, { responseType: 'blob' })
      .subscribe({
        next: blob => saveAs(blob, `import-log-${uuid || 'unknown'}.txt`),
        error: error => console.error('[AsyncTaskProgress] Error downloading import log:', error),
      });
  }


  /**
   * Check if task has an actionable result
   */
  hasActionableResult(task: AsyncTask): boolean {
    // Check if task has an action button label and meets the conditions for showing it
    if (!task.actionButtonLabel) {
      return false;
    }

    // Autocrop with vertical variant
    if (task.type === 'autocrop' && !!task.result?.vertical_variant?.uuid) {
      return true;
    }

    // Transcript generation with mediaAssetUuid
    if ((task.type === 'speech-to-text' || task.type === 'speech-to-words') && !!task.metadata?.mediaAssetUuid) {
      return true;
    }

    // CC generation with output file
    if (task.type === 'generate-cc' && !!task.result?.output_file) {
      return true;
    }

    // Medvideo extraction with highlights
    if (task.type === 'medvideo-extract' && task.result?.highlights?.length > 0) {
      return true;
    }

    // Campaign transcript report ready to download
    if (task.type === 'campaign-transcript-report' && !!task.result) {
      return true;
    }

    // Billing backfill always shows a "View Billing" action on completion
    if (task.type === 'billing-backfill') {
      return true;
    }

    // AI labeling: only when proposed new labels are waiting for a decision
    if (task.type === 'media-autolabel' && (task.result?.pendingProposals ?? 0) > 0) {
      return true;
    }

    // A finished merge is a draft: the preview URL is the handle on it, and this
    // button is the only route into the review dialog.
    if (task.type === 'media-merge' && !!task.result?.previewUrl) {
      return true;
    }

    // A save that failed put the draft back and re-armed its preview URL — same
    // button, so the user can retry without redoing the merge.
    if (task.type === 'media-merge-save' && !!task.result?.previewUrl) {
      return true;
    }

    // Anything that produced a file the server still holds — the card keeps a Download
    // button so the file is reachable after the automatic save was missed.
    if (task.resultUrl) {
      return true;
    }

    return false;
  }

  /**
   * Get action button label from task definition
   */
  getActionLabel(task: AsyncTask): string {
    return task.actionButtonLabel || 'View Result';
  }

  /** Icon for the completion action; AI labeling keeps the auto_awesome mark it uses elsewhere. */
  getActionIcon(task: AsyncTask, fallback: string): string {
    if (task.type === 'media-autolabel') {
      return 'auto_awesome';
    }
    // A task that produced a server-held file offers a Download button.
    return task.resultUrl ? 'download' : fallback;
  }

  private transformTranscriptToAuditData(transcriptReport: any, fallbackStartedAt?: string): AuditReportData {
    const assetMap = new Map<string, AuditReportAsset>();

    for (const campaign of (transcriptReport.campaigns ?? [])) {
      for (const task of (campaign.tasks ?? [])) {
        const asset = task.mediaAsset;
        if (!asset?.uuid) continue;

        if (!assetMap.has(asset.uuid)) {
          const senderCreatedBy = (task.senderFirstname || task.senderEmail)
            ? {
                firstName: task.senderFirstname,
                lastName: task.senderLastname,
                email: task.senderEmail,
                phone: task.senderPhone,
              }
            : asset.createdBy;

          assetMap.set(asset.uuid, {
            uuid: asset.uuid,
            name: asset.name,
            filename: asset.filename,
            txt: asset.txt,
            vtt: asset.vtt,
            createdBy: senderCreatedBy,
            lastUpdated: task.lastUpdated ?? fallbackStartedAt,
            tasks: [],
          });
        }

        assetMap.get(asset.uuid)!.tasks!.push({
          token: task.token,
          status: task.status,
          lastUpdated: task.lastUpdated ?? fallbackStartedAt,
          campaignTitle: campaign.campaignTitle,
          campaignName: campaign.campaignName,
          receiverFirstname: task.receiverFirstname,
          receiverLastname: task.receiverLastname,
          receiverEmail: task.receiverEmail,
          receiverPhone: task.receiverPhone,
          receiverExternalId: task.receiverExternalId,
          senderFirstname: task.senderFirstname,
          senderLastname: task.senderLastname,
          senderEmail: task.senderEmail,
          senderPhone: task.senderPhone,
          senderExternalId: task.senderExternalId,
        });
      }
    }

    return {
      assets: Array.from(assetMap.values()),
      generatedAt: new Date().toLocaleString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      }),
    };
  }
}
